
import moment from "moment-timezone";

import { isEmpty } from './isEmpty';

/** Site display timezone (matches live/upcoming match logic). */
export const DISPLAY_TIMEZONE = "America/Chicago";

export const getTimezoneAbbrev = (dateTime) => {
    try {
        if (isEmpty(dateTime)) return "";
        return moment.tz(dateTime, DISPLAY_TIMEZONE).format("z");
    } catch (err) {
        return "";
    }
};

/**
 * Calendar-aware commence label (America/Chicago):
 * - same day → "Today - hh:mm A"
 * - next day → "Tomorrow - hh:mm A"
 * - otherwise → "Tue, Oct 28 - 07:10 PM"
 */
export const formatCommenceTime = (dateTime, now = undefined) => {
    try {
        if (isEmpty(dateTime)) return "";
        const kickoff = moment.tz(dateTime, DISPLAY_TIMEZONE);
        if (!kickoff.isValid()) return "";

        const today = moment.tz(now || undefined, DISPLAY_TIMEZONE).startOf("day");
        const gameDay = kickoff.clone().startOf("day");
        const timePart = kickoff.format("hh:mm A");

        if (gameDay.isSame(today, "day")) {
            return `Today - ${timePart}`;
        }
        if (gameDay.isSame(today.clone().add(1, "day"), "day")) {
            return `Tomorrow - ${timePart}`;
        }
        return `${kickoff.format("ddd, MMM D")} - ${timePart}`;
    } catch (err) {
        return "";
    }
};

/**
 * Event kickoff for slips, history legs, and compact rows.
 * Uses {@link formatCommenceTime}; optionally appends Chicago TZ abbrev.
 */
export const formatEventKickoff = (
    dateTime,
    { uppercase = false, includeTz = false, now } = {}
) => {
    let label = formatCommenceTime(dateTime, now);
    if (!label) return "";
    if (includeTz) {
        const tz = getTimezoneAbbrev(dateTime);
        if (tz) label = `${label} ${tz}`;
    }
    return uppercase ? label.toUpperCase() : label;
};

/** Slip lines that previously used momentFormatUpperCase on gameTime. */
export const formatEventKickoffUpperCase = (dateTime, options = {}) =>
    formatEventKickoff(dateTime, { ...options, uppercase: true, includeTz: true });

/** When an entry was placed (same calendar rules as kickoff). */
export const formatPlacedAt = (dateTime, now) =>
    formatEventKickoff(dateTime, { includeTz: true, now });

/** Call-bet cards: match kickoff when set, else order created time. */
export const formatCallBetCardTime = (gameTime, createdAt) => {
    if (!isEmpty(gameTime)) {
        return formatEventKickoff(gameTime, { includeTz: true });
    }
    return momentFormat(createdAt, "ddd, MMM D - hh:mm A z");
};

export const momentFormat = (dateTime, format = "dddd - hh:mm A z") => {
    try {
        if (!isEmpty(dateTime)) {
            return moment.tz(dateTime, DISPLAY_TIMEZONE).format(format);
        }
        return "";
    } catch (err) {
        return "";
    }
};

/**
 * @deprecated Prefer {@link formatEventKickoffUpperCase} for game kickoff times.
 * Still used when a custom moment format string is required.
 */
export const momentFormatUpperCase = (dateTime, format = "ddd - hh:mm A z") => {
    try {
        if (!isEmpty(dateTime)) {
            return moment.tz(dateTime, DISPLAY_TIMEZONE).format(format).toUpperCase();
        }
        return "";
    } catch (err) {
        return "";
    }
};
